import { seasonLabel } from "../../../services/seasons/seasonService";
import styles from "./SeasonPicker.module.css";

/**
 * Which season am I looking at, and which week of it.
 *
 * THE NAMING PROBLEM THIS COMPONENT EXISTS TO SOLVE
 * -------------------------------------------------
 * The season that starts in August 2026 and ends with a Super Bowl in
 * February 2027 is called the 2026 season by the NFL, by ESPN and by every
 * feed this app reads. Plenty of people watching it will call it "the 2027
 * season", because that is the year the final is played in. A dropdown
 * showing a bare list of years would leave both of them guessing.
 *
 * So the year never appears on its own. The season being played is marked
 * as such in the list, and the span underneath says in months and years
 * exactly what the selected entry covers: "Aug 2026 to Feb 2027". Those
 * dates are ESPN's own, not ours.
 *
 * WEEKS, AND WHY THEY ONLY APPEAR FOR A PAST SEASON
 * -------------------------------------------------
 * "Coming up" is a sentence about a season that is still being played. A
 * season that finished has no coming up, so browsing it means picking a
 * week. The phases and the weeks in them come from ESPN's calendar for
 * that particular season, which is the only honest source: the 2015
 * regular season had seventeen weeks and 2025 had eighteen, and the
 * postseason rounds are called Wild Card, Divisional Round, Conference
 * Championship, Pro Bowl and Super Bowl in the payload itself. Nothing in
 * this file knows any of those names.
 *
 * Renders nothing at all until the season list has arrived. A page that
 * cannot reach ESPN's season endpoints is the page it was before this
 * component existed.
 */
const SeasonPicker = ({
  years = [],
  currentYear,
  selectedYear,
  spanLabel,
  isArchive = false,
  phases = [],
  activeWeek,
  onSelectSeason,
  onSelectWeek,
  showSeason = true,
  showWeeks = true,
  showDetails = true,
  compact = false,
  idPrefix = "season",
  className = "",
}) => {
  const canShowSeason = showSeason && years.length > 0 && selectedYear != null;
  const canShowWeeks = showWeeks && isArchive && phases.length > 0;

  if (!canShowSeason && !canShowWeeks) return null;

  const rootClassName = [
    styles.SeasonPicker,
    compact ? styles.compact : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const activePhase = phases.find(
    (phase) => phase.seasonTypeId === activeWeek?.seasonType
  );
  const seasonId = `${idPrefix}-year`;
  const phaseId = `${idPrefix}-phase`;
  const weekId = `${idPrefix}-week`;

  const handlePhase = (event) => {
    const seasonTypeId = Number(event.target.value);
    const phase = phases.find((entry) => entry.seasonTypeId === seasonTypeId);
    const first = phase?.weeks?.[0];
    if (first) onSelectWeek(seasonTypeId, first.number);
  };

  const handleWeek = (event) => {
    if (!activePhase) return;
    onSelectWeek(activePhase.seasonTypeId, Number(event.target.value));
  };

  return (
    <section
      className={rootClassName}
      aria-label="Choose a season"
    >
      <div className={styles.controls}>
        {canShowSeason && (
          <div className={styles.field}>
            <label className={styles.label} htmlFor={seasonId}>
              Season
            </label>
            <select
              id={seasonId}
              className="form-select"
              value={selectedYear}
              onChange={(event) => onSelectSeason(Number(event.target.value))}
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {`${seasonLabel(year)}${year === currentYear ? " (on now)" : ""}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {canShowWeeks && (
          <>
            <div className={styles.field}>
              <label className={styles.label} htmlFor={phaseId}>
                Part of the season
              </label>
              <select
                id={phaseId}
                className="form-select"
                value={activePhase?.seasonTypeId ?? ""}
                onChange={handlePhase}
              >
                {phases.map((phase) => (
                  <option key={phase.seasonTypeId} value={phase.seasonTypeId}>
                    {phase.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor={weekId}>
                Week
              </label>
              <select
                id={weekId}
                className="form-select"
                value={activeWeek?.week ?? ""}
                onChange={handleWeek}
              >
                {(activePhase?.weeks ?? []).map((week) => (
                  <option key={week.number} value={week.number}>
                    {week.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      {showDetails && canShowSeason && (
        <p className={styles.span}>
          <strong>{seasonLabel(selectedYear)}</strong>
          {spanLabel ? `: ${spanLabel}.` : "."}
        </p>
      )}
    </section>
  );
};

export default SeasonPicker;
